import { SkyChatApi } from '@skychat/shared-core';
import { io, type Socket } from 'socket.io-client';

export type GameServiceConfig = {
  apiBaseUrl: string;
  socketUrl: string;
  socketPath: string;
};

type ClientConfigService = {
  apiBaseUrl?: string;
  socketUrl?: string;
  socketPath?: string;
  baseUrl?: string;
  url?: string;
};

type ClientConfigResponse = {
  services?: { games?: ClientConfigService; main?: ClientConfigService };
  gameService?: ClientConfigService;
  gameServerUrl?: string;
  gameApiBaseUrl?: string;
  gameSocketUrl?: string;
  gameSocketPath?: string;
};

const CLIENT_CONFIG_PATH = '/api/client-config';
const DEFAULT_GAME_SOCKET_PATH = '/game-socket.io';
const DEFAULT_LOCAL_GAME_PORT = '4010';
const GAME_SOCKET_EVENTS = [
  'game:invite',
  'game:update',
  'game:party:invite',
  'game:party:invite:response',
  'game:party:ready',
  'game:party:start',
  'game:session:progress',
  'game:session:chat',
  'game:spectate:update',
  'game:spectate:chat',
  'game:lobby:presence'
] as const;

export function isPrivateDevHostname(hostname: string): boolean {
  const normalized = String(hostname || '').trim().toLowerCase();
  if (!normalized) return false;
  if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(normalized)) return true;
  if (normalized.endsWith('.local')) return true;
  if (/^10(?:\.\d{1,3}){3}$/.test(normalized)) return true;
  if (/^192\.168(?:\.\d{1,3}){2}$/.test(normalized)) return true;
  return /^172\.(1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2}$/.test(normalized);
}

export function runtimeApiBaseUrl(defaultBaseUrl = 'https://m.chat.webhelloworld.it'): string {
  if (typeof window === 'undefined') return defaultBaseUrl;
  const { hostname, origin, protocol } = window.location;
  if (!/^https?:$/i.test(protocol) || !isPrivateDevHostname(hostname)) return defaultBaseUrl;
  return origin || defaultBaseUrl;
}

function mobileGameDebugEnabled(): boolean {
  try {
    const win = window as typeof window & { __SKYCHAT_GAME_NET_DEBUG__?: boolean };
    if (win.__SKYCHAT_GAME_NET_DEBUG__ === true) return true;
    if (window.localStorage?.getItem('skychat_game_network_debug') === 'true') return true;
    const params = new URLSearchParams(window.location.search || '');
    return params.get('gameNetDebug') === '1' || params.get('gameNetDebug') === 'true';
  } catch {
    return false;
  }
}

function mobileEstimateBytes(value: unknown): number {
  try {
    return new TextEncoder().encode(JSON.stringify(value ?? null)).length;
  } catch {
    return 0;
  }
}

function mobileTransportName(socket: Socket | null | undefined): string | null {
  const raw = socket as Socket & { io?: { engine?: { transport?: { name?: string } } } } | null | undefined;
  return raw?.io?.engine?.transport?.name || null;
}

function installMobileGameSocketDiagnostics(socket: Socket, gameConfig: GameServiceConfig): Socket {
  const rawSocket = socket as Socket & { __skychatGameNetDiagnostics?: boolean; emit: (...args: unknown[]) => Socket };
  if (!rawSocket || rawSocket.__skychatGameNetDiagnostics) return socket;
  rawSocket.__skychatGameNetDiagnostics = true;
  let seq = 0;
  const originalEmit = rawSocket.emit.bind(rawSocket);
  rawSocket.emit = (eventName: unknown, payload?: unknown, ...rest: unknown[]) => {
    const event = String(eventName || '');
    let nextPayload = payload;
    if (event.startsWith('game:') && mobileGameDebugEnabled() && nextPayload && typeof nextPayload === 'object' && !Array.isArray(nextPayload)) {
      const data = nextPayload as Record<string, unknown>;
      if (!data.__net) {
        nextPayload = {
          ...data,
          __net: {
            id: `mobile-game-${Date.now()}-${++seq}`,
            event,
            clientSentAt: Date.now(),
            payloadBytes: mobileEstimateBytes(data),
            source: 'apps/mobile/src/app/AppContext.tsx'
          }
        };
      }
    }
    if (event.startsWith('game:') && mobileGameDebugEnabled()) {
      console.log('[GameNet][mobile.emit]', {
        event,
        payloadBytes: mobileEstimateBytes(nextPayload),
        transport: mobileTransportName(socket),
        socketPath: gameConfig.socketPath || DEFAULT_GAME_SOCKET_PATH,
        payload: nextPayload
      });
    }
    return originalEmit(event, nextPayload, ...rest);
  };
  socket.on('connect', () => {
    console.warn('[GameNet][mobile.connect]', {
      socketId: socket.id,
      apiBaseUrl: gameConfig.apiBaseUrl,
      socketUrl: gameConfig.socketUrl || gameConfig.apiBaseUrl,
      socketPath: gameConfig.socketPath || DEFAULT_GAME_SOCKET_PATH,
      transport: mobileTransportName(socket)
    });
  });
  socket.on('disconnect', (reason) => {
    console.warn('[GameNet][mobile.disconnect]', {
      socketId: socket.id,
      reason,
      transport: mobileTransportName(socket)
    });
  });
  socket.on('connect_error', (error: Error) => {
    console.warn('[GameNet][mobile.connect_error]', {
      message: error.message || String(error),
      socketUrl: gameConfig.socketUrl || gameConfig.apiBaseUrl,
      socketPath: gameConfig.socketPath || DEFAULT_GAME_SOCKET_PATH
    });
  });
  socket.onAny((event, payload) => {
    if (!String(event || '').startsWith('game:') || !mobileGameDebugEnabled()) return;
    console.log('[GameNet][mobile.receive]', {
      event,
      payloadBytes: mobileEstimateBytes(payload),
      transport: mobileTransportName(socket),
      payload
    });
  });
  return socket;
}

function normalizeBaseUrlSafe(value = ''): string {
  return String(value || '').trim().replace(/\/+$/, '');
}

function buildLocalGameBaseUrl(mainBaseUrl: string): string {
  const base = normalizeBaseUrlSafe(mainBaseUrl);
  if (!base) return '';
  try {
    const url = new URL(base);
    const host = url.hostname.toLowerCase();
    const isLocalHost = ['localhost', '127.0.0.1', '::1', '[::1]'].includes(host) || isPrivateDevHostname(host);
    if (isLocalHost && (url.port === '4000' || url.port === '5174' || url.port === '5173' || !url.port)) {
      url.port = DEFAULT_LOCAL_GAME_PORT;
      return normalizeBaseUrlSafe(url.toString());
    }
  } catch {}
  return base;
}

export function buildFallbackGameServiceConfig(mainBaseUrl: string): GameServiceConfig {
  const gameBaseUrl = buildLocalGameBaseUrl(mainBaseUrl);
  return { apiBaseUrl: gameBaseUrl, socketUrl: gameBaseUrl, socketPath: DEFAULT_GAME_SOCKET_PATH };
}

export function parseGameServiceConfig(mainBaseUrl: string, raw: ClientConfigResponse | null): GameServiceConfig {
  const fallback = buildFallbackGameServiceConfig(mainBaseUrl);
  const game = raw?.services?.games || raw?.gameService || {};
  const apiBaseUrl = normalizeBaseUrlSafe(
    game.apiBaseUrl || game.baseUrl || game.url || raw?.gameApiBaseUrl || raw?.gameServerUrl || fallback.apiBaseUrl
  );
  const socketUrl = normalizeBaseUrlSafe(game.socketUrl || raw?.gameSocketUrl || apiBaseUrl || fallback.socketUrl);
  const socketPath = String(game.socketPath || raw?.gameSocketPath || fallback.socketPath || DEFAULT_GAME_SOCKET_PATH);
  return { apiBaseUrl, socketUrl, socketPath };
}

export async function loadGameServiceConfig(mainBaseUrl: string): Promise<GameServiceConfig> {
  const fallback = buildFallbackGameServiceConfig(mainBaseUrl);
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller && typeof window !== 'undefined' ? window.setTimeout(() => controller.abort(), 2500) : null;
  try {
    const response = await fetch(`${normalizeBaseUrlSafe(mainBaseUrl)}${CLIENT_CONFIG_PATH}`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller?.signal
    });
    const json = await response.json().catch(() => null) as ClientConfigResponse | null;
    if (!response.ok || !json || typeof json !== 'object') return fallback;
    return parseGameServiceConfig(mainBaseUrl, json);
  } catch {
    return fallback;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
}

function withGamePath(gameBaseUrl: string, path: string): string {
  const base = normalizeBaseUrlSafe(gameBaseUrl);
  return base ? `${base}${path}` : path;
}

async function gameJsonRequest(gameBaseUrl: string, token: string | null, path: string, options: { method?: string; json?: unknown } = {}): Promise<Record<string, unknown>> {
  const headers: Record<string, string> = {};
  if (options.json !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(withGamePath(gameBaseUrl, path), {
    method: options.method || (options.json === undefined ? 'GET' : 'POST'),
    headers,
    body: options.json === undefined ? undefined : JSON.stringify(options.json)
  });
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok && data.ok !== false) {
    return { ok: false, error: String(data.error || data.message || `HTTP ${response.status}`), status: response.status };
  }
  return data;
}

function encodePathPart(value: string): string {
  return encodeURIComponent(String(value || '').trim());
}

export function normalizeGameApiKey(value: unknown): string {
  const raw = typeof value === 'object' && value
    ? String((value as Record<string, unknown>).gameType || (value as Record<string, unknown>).gameId || (value as Record<string, unknown>).gameKey || '')
    : String(value || '');
  const key = raw.trim().toLowerCase().replace(/\s+/g, '').replace(/_/g, '-');
  if (key === 'game2048' || key === '2048') return '2048';
  if (['gameminesweeper', 'game-minesweeper', 'mines', 'minesweeper'].includes(key)) return 'minesweeper';
  if (['gamesudoku', 'game-sudoku', 'sudoku'].includes(key)) return 'sudoku';
  if (['number-huarong', 'numberhuarong', 'huarong', '数字华容道'].includes(key)) return 'number-huarong';
  if (key === 'connect-4' || key === 'connectfour') return 'connect4';
  if (key === 'battaglia-navale') return 'battleship';
  return key;
}

function leaderboardPathForGame(value: unknown): string {
  const key = normalizeGameApiKey(value);
  if (key === 'general') return '/api/games/general/leaderboard';
  if (key === '2048') return '/api/games/2048/leaderboard';
  if (key === 'minesweeper') {
    const difficulty = typeof value === 'object' && value ? String((value as Record<string, unknown>).difficulty || 'easy') : 'easy';
    return `/api/games/minesweeper/leaderboard?difficulty=${encodeURIComponent(difficulty)}`;
  }
  if (key === 'card-flip' || key === 'cardflip') return '/api/games/card-flip/leaderboard';
  return `/api/games/board/${encodePathPart(key)}/leaderboard`;
}

function recordPathForGame(value: unknown): string {
  const key = normalizeGameApiKey(value);
  if (key === '2048') return '/api/games/2048/record';
  if (key === 'minesweeper') return '/api/games/minesweeper/record';
  if (key === 'card-flip' || key === 'cardflip') return '/api/games/card-flip/record';
  return '/api/games/board-record';
}

function buildRecordPayload(gameId: unknown, payload: Record<string, unknown>): Record<string, unknown> {
  const key = normalizeGameApiKey(gameId);
  if (key === '2048') return { score: Number(payload.score || 0) };
  if (key === 'minesweeper') {
    const timeMs = Number(payload.timeMs || payload.time_ms || payload.elapsedMs || payload.score || 0);
    return { ...payload, mode: payload.mode || 'single', difficulty: payload.difficulty || 'easy', timeMs: Math.max(1, timeMs) };
  }
  if (key === 'card-flip' || key === 'cardflip') {
    return { score: Number(payload.score || 0), timeElapsed: Number(payload.timeElapsed || payload.time_elapsed || 0), moves: Number(payload.moves || 0) };
  }
  return { gameKey: key, result: payload.result || (payload.won === true ? 'win' : payload.won === false ? 'loss' : 'draw') };
}

export function attachSeparatedGameApi(client: SkyChatApi, gameBaseUrl: string, token: string | null): void {
  const target = client as SkyChatApi & Record<string, unknown>;
  target.listGameSessions = () => gameJsonRequest(gameBaseUrl, token, '/api/games?lite=1');
  target.getGameSession = (gameId: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/snapshot`);
  target.createGameSession = (payload: unknown) => gameJsonRequest(gameBaseUrl, token, '/api/games/invite', { method: 'POST', json: payload || {} });
  target.startSoloGame = (payload: unknown = {}) => gameJsonRequest(gameBaseUrl, token, '/api/games/solo', { method: 'POST', json: payload || {} });
  target.startSudokuSolo = (payload: unknown = {}) => gameJsonRequest(gameBaseUrl, token, '/api/games/sudoku/solo', { method: 'POST', json: payload || {} });
  target.respondGameInvite = (gameId: unknown, accepted: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/${accepted ? 'accept' : 'reject'}`, { method: 'POST', json: {} });
  target.readyGameSession = (gameId: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/ready`, { method: 'POST', json: {} });
  target.startGameSession = (gameId: unknown, payload: unknown = {}) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/start`, { method: 'POST', json: payload || {} });
  target.abandonGameSession = (gameId: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/abandon`, { method: 'POST', json: {} });
  target.gameAction = (gameId: unknown, payload: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/action`, { method: 'POST', json: payload || {} });
  target.gameMove = (gameId: unknown, payload: unknown) => gameJsonRequest(gameBaseUrl, token, `/api/games/${encodePathPart(String(gameId || ''))}/move`, { method: 'POST', json: payload || {} });
  target.saveGameRecord = (gameId: unknown, payload: unknown) => gameJsonRequest(gameBaseUrl, token, recordPathForGame(gameId), { method: 'POST', json: buildRecordPayload(gameId, (payload || {}) as Record<string, unknown>) });
  target.listGameRecords = (arg: unknown) => gameJsonRequest(gameBaseUrl, token, leaderboardPathForGame(arg));
  target.getGameRecords = target.listGameRecords;
  target.listGameLeaderboard = target.listGameRecords;
  target.getGameLeaderboard = target.listGameRecords;
  target.listLeaderboards = target.listGameRecords;
  target.getLeaderboards = target.listGameRecords;
  target.gameLeaderboards = target.listGameRecords;
}

export function connectSeparatedGameSocket(gameConfig: GameServiceConfig, token: string | null, onGameEvent: () => void, device?: { deviceKind?: string; deviceSessionId?: string }): Socket {
  const socket = installMobileGameSocketDiagnostics(io(gameConfig.socketUrl || gameConfig.apiBaseUrl, {
    path: gameConfig.socketPath || DEFAULT_GAME_SOCKET_PATH,
    transports: ['websocket']
  }), gameConfig);
  socket.on('connect', () => {
    if (token) socket.emit('auth', {
      token,
      ...(device?.deviceKind ? { deviceKind: device.deviceKind } : {}),
      ...(device?.deviceSessionId ? { deviceSessionId: device.deviceSessionId } : {})
    });
  });
  socket.on('connect_error', (error: Error) => console.warn('[GameSocket] connect_error:', error.message || error));
  GAME_SOCKET_EVENTS.forEach((eventName) => socket.on(eventName, onGameEvent));
  return socket;
}
